'use client';

import { useEffect, useState } from 'react';
import { IntlProvider, type AbstractIntlMessages } from 'use-intl';
import { isSupportedLocale, type SupportedLocale } from '@/i18n/config';
import { loadMessages } from '@/lib/i18n/messages';
import { useSessionStore } from '@/lib/stores/session.store';

/**
 * Client i18n provider on `use-intl` (framework-agnostic core of next-intl).
 * Replaces `NextIntlClientProvider`. Messages for the starting locale are
 * loaded in `main.tsx` before mount, so the whole tree renders translated on
 * first paint (no untranslated flash). `timeZone` matches the former server
 * config.
 *
 * After that it follows the signed-in user's locale: a switch in the menu or
 * the settings, and a stored locale that differs from the one the page
 * started in, load the new bundle and swap it in place. Without this the
 * language only changed on the next page load (KAN-1943).
 */
export function AppIntlProvider({
  locale: initialLocale,
  messages: initialMessages,
  children,
}: {
  locale: SupportedLocale;
  messages: AbstractIntlMessages;
  children: React.ReactNode;
}) {
  const [active, setActive] = useState({ locale: initialLocale, messages: initialMessages });
  const profileLocale = useSessionStore((s) => s.profile?.locale);

  useEffect(() => {
    if (!isSupportedLocale(profileLocale) || profileLocale === active.locale) return;
    // A rollback or a second switch while the bundle loads supersedes this one.
    let superseded = false;
    loadMessages(profileLocale)
      .then((messages) => {
        if (superseded) return;
        document.documentElement.lang = profileLocale;
        setActive({ locale: profileLocale, messages });
      })
      .catch((err) => console.warn('[i18n] could not load messages for', profileLocale, err));
    return () => {
      superseded = true;
    };
  }, [profileLocale, active.locale]);

  return (
    <IntlProvider locale={active.locale} messages={active.messages} timeZone="Europe/Berlin">
      {children}
    </IntlProvider>
  );
}
